import { Activity, Crosshair, Globe2, Radar, ShieldAlert, Newspaper, SlidersHorizontal } from 'lucide-react';
import type { DashboardData, FeedSource } from '../types';
import { categoryText, threatText, type InterfaceLanguage, uiText } from '../lib/i18n';
import SourceQualityPanel from './SourceQualityPanel';
import CommandCenter from './CommandCenter';

export interface Layers { news: boolean; military: boolean; conflict: boolean; threat: boolean; }
interface Props {
  language: InterfaceLanguage;
  stats: DashboardData['stats'];
  refreshing: boolean;
  layers: Layers;
  onLayerChange: (layers: Layers) => void;
  category: string;
  onCategoryChange: (value: string) => void;
  threat: string;
  onThreatChange: (value: string) => void;
  range: string;
  onRangeChange: (value: string) => void;
  sources: FeedSource[];
}

const LAYER_META = [
  { key: 'news', label: 'समाचार घटनाएँ', hint: 'वैश्विक रिपोर्टिंग', Icon: Newspaper, tone: 'cyan' },
  { key: 'military', label: 'सैन्य गतिविधि', hint: 'सार्वजनिक रिपोर्ट', Icon: Crosshair, tone: 'blue' },
  { key: 'conflict', label: 'संघर्ष क्षेत्र', hint: 'समाचार वर्गीकरण', Icon: ShieldAlert, tone: 'orange' },
  { key: 'threat', label: 'ऊँचे खतरे', hint: 'उच्च / गंभीर संकेत', Icon: Radar, tone: 'red' }
] as const;
const CATEGORIES = ['सामान्य समाचार', 'संघर्ष', 'सैन्य गतिविधि', 'कूटनीति', 'आपदा', 'साइबर', 'राजनीति', 'अर्थव्यवस्था'];
const THREATS = ['गंभीर', 'उच्च', 'मध्यम', 'निम्न'];

export default function ControlPanel(props: Props) {
  const t = (text: string) => uiText(props.language, text);
  const online = props.sources.filter((source) => source.ok).length;
  const checked = props.sources.filter((source) => source.checkedAt).length;
  const progress = props.sources.length ? (online / props.sources.length) * 100 : 0;
  return (
    <aside className="control-panel">
      <div className="section-heading"><span>01 / COMMAND CENTER</span><Activity size={14} /></div>
      <CommandCenter stats={props.stats} refreshing={props.refreshing} />
      <div className="section-heading"><span>02 / {t('FILTERS')}</span><SlidersHorizontal size={14} /></div>

      <div className="filter-row">
        <label><span>{t('श्रेणी')}</span><select value={props.category} onChange={(event) => props.onCategoryChange(event.target.value)}><option value="all">{t('सभी विषय')}</option>{CATEGORIES.map((category) => <option key={category} value={category}>{categoryText(props.language, category)}</option>)}</select></label>
        <label><span>{t('खतरा')}</span><select value={props.threat} onChange={(event) => props.onThreatChange(event.target.value)}><option value="all">{t('सभी स्तर')}</option>{THREATS.map((threat) => <option key={threat} value={threat}>{threatText(props.language, threat)}</option>)}</select></label>
      </div>
      <label className="field-label range-label">{t('समय सीमा')}</label>
      <div className="range-buttons">{[['24h', '24 घंटे'], ['72h', '3 दिन'], ['7d', '7 दिन'], ['all', 'सभी']].map(([value, label]) => <button key={value} className={props.range === value ? 'range-active' : ''} onClick={() => props.onRangeChange(value)}>{t(label)}</button>)}</div>

      <div className="section-heading layer-heading"><span>03 / {t('MAP LAYERS')}</span><Globe2 size={14} /></div>
      <div className="layer-list">
        {LAYER_META.map(({ key, label, hint, Icon, tone }) => <button className="layer-toggle" key={key} onClick={() => props.onLayerChange({ ...props.layers, [key]: !props.layers[key] })} aria-pressed={props.layers[key]}>
          <span className={`layer-icon ${tone}`}><Icon size={15} /></span><span className="layer-copy"><strong>{t(label)}</strong><small>{t(hint)}</small></span><span className={`switch ${props.layers[key] ? 'switch-on' : ''}`}><i /></span>
        </button>)}
      </div>

      <div className="section-heading source-heading"><span>04 / {t('SOURCE HEALTH')}</span><Activity size={14} /></div>
      <div className="source-health-card">
        <div className="health-numbers"><strong>{online}<small>/{props.sources.length}</small></strong><span>{checked ? t('जाँचे गए स्रोत') : t('पहली जाँच प्रतीक्षित')}</span></div>
        <div className="health-track"><span style={{ width: `${progress}%` }} /></div>
        <div className="health-meta"><span><i className="status-dot" /> {online} {t('उपलब्ध')}</span><span>{checked - online > 0 ? `${checked - online} ${t('त्रुटि')}` : t('स्रोत जाँच')}</span></div>
        <p>{t('फ़ीड उपलब्धता नेटवर्क और प्रकाशक पर निर्भर है।')}</p>
      </div>
      <SourceQualityPanel language={props.language} sources={props.sources} />
      <div className="local-ai-note"><div className="ai-mark">AI</div><div><strong>{t('स्थानीय वर्गीकरण')}</strong><small>{t('Rules v1 · कोई cloud key नहीं')}</small></div><span className="local-pill">LOCAL</span></div>
      <div className="panel-footnote"><span>{t('डेटा स्रोत')}</span><strong>{t('सार्वजनिक RSS')}</strong><span>{t('तरीका')}</span><strong>{t('स्वचालित · सत्यापन आवश्यक')}</strong></div>
    </aside>
  );
}
